/**
 * Offline Sync Service
 * 
 * Handles syncing pending entries from IndexedDB to Firestore
 * Uses Background Sync API when available, falls back to manual sync
 */

import {
  queueEntry,
  getPendingEntries,
  removePendingEntry,
  updateRetryCount,
  type PendingEntry,
  type EntryType,
} from './offlineQueue.service';
import { saveNutritionEntryDirect } from './nutrition.service';
import { saveWorkoutEntryDirect } from './workout.service';
import { analyzeNutrition } from './openai/nutrition.service';
import type { NutritionEntry } from '../types/nutrition';
import type { WorkoutEntry } from '../types/workout';

const MAX_RETRY_COUNT = 5;

/**
 * Check if Background Sync API is supported
 */
export function isBackgroundSyncSupported(): boolean {
  return 'serviceWorker' in navigator && 'sync' in (ServiceWorkerRegistration.prototype as unknown as Record<string, unknown>);
}

/**
 * Register Background Sync for offline entries
 */
export async function registerBackgroundSync(tag: string = 'sync-pending-entries'): Promise<void> {
  if (!isBackgroundSyncSupported()) {
    console.log('[OfflineSync] Background Sync not supported');
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    await (registration as ServiceWorkerRegistration & { sync?: { register: (tag: string) => Promise<void> } }).sync?.register(tag);
    console.log('[OfflineSync] Background Sync registered:', tag);
  } catch (error) {
    console.error('[OfflineSync] Failed to register Background Sync:', error);
  }
}

/**
 * Result of syncing a pending entry
 */
interface SyncResult {
  success: boolean;
  savedEntryId?: string;
  savedMacros?: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fibre: number;
  };
  pendingId?: string; // Original pending ID for updating recents
}

/**
 * Sync a single pending entry
 */
async function syncPendingEntry(entry: PendingEntry): Promise<SyncResult> {
  try {
    if (entry.type === 'nutrition') {
      const nutritionEntry = entry.entryData as Omit<NutritionEntry, 'id' | 'userId' | 'date' | 'timestamp'>;
      
      // Bug Fix v23: Allow zero-macro entries that were created offline (they have placeholder 1 kcal)
      // Only reject entries that are truly invalid (no macros object at all, or all NaN/Infinity)
      if (nutritionEntry.macros) {
        const totalCalories = nutritionEntry.macros.calories || 0;
        const totalMacros = (nutritionEntry.macros.protein || 0) + 
                           (nutritionEntry.macros.carbs || 0) + 
                           (nutritionEntry.macros.fat || 0);
        
        // Bug Fix v23: Allow entries with 1 kcal placeholder (created offline) or entries with valid macros
        // Only reject entries that are truly invalid (all zero AND not a placeholder)
        const isPlaceholder = totalCalories === 1 && totalMacros === 0;
        const hasValidMacros = totalCalories > 0 || totalMacros > 0;
        
        if (!isPlaceholder && !hasValidMacros) {
          console.error('[OfflineSync] Rejecting nutrition entry with all-zero macros (not a placeholder):', {
            entryId: entry.id,
            description: nutritionEntry.description,
            source: nutritionEntry.source,
            calories: totalCalories,
            macros: totalMacros,
          });
          // Remove invalid entry from queue
          await removePendingEntry(entry.id);
          return { success: false };
        }
        
        // BUG FIX #6: Check if entry needs parsing (parseStatus='pending' or placeholder with description)
        const needsParsing = nutritionEntry.parseStatus === 'pending' || (isPlaceholder && nutritionEntry.description);
        
        // Bug Fix v27: Process placeholder entries with AI after sync to get real macros
        // BUG FIX #6: Also process entries with parseStatus='pending'
        // CRITICAL: Update entry data BEFORE saving to ensure macros are applied correctly
        if (needsParsing && nutritionEntry.description) {
          console.log('[OfflineSync] BUG FIX #6: Processing entry that needs parsing:', {
            entryId: entry.id,
            description: nutritionEntry.description,
            parseStatus: nutritionEntry.parseStatus,
            isPlaceholder,
          });
          
          try {
            // Trigger AI analysis to get real macros
            const analysis = await analyzeNutrition({
              textDescription: nutritionEntry.description,
              userId: entry.userId,
            });
            
            // Bug Fix v27: Update the entry data with real macros BEFORE saving
            // BUG FIX #6: Remove parseStatus='pending' and mark as completed
            // This ensures the entry is saved with correct macros from the start
            const updatedEntry = {
              ...nutritionEntry,
              macros: {
                calories: analysis.calories || 0,
                protein: analysis.protein || 0,
                carbs: analysis.carbs || 0,
                fat: analysis.fat || 0,
                fibre: analysis.fibre || 0,
              },
              aiExtracted: true,
              source: nutritionEntry.source || 'text-ai-estimate',
              parseStatus: 'completed' as const, // BUG FIX #6: Mark as completed
            };
            
            // Save entry with correct macros already applied
            const savedEntry = await saveNutritionEntryDirect(entry.userId, updatedEntry, entry.date);
            console.log('[OfflineSync] BUG FIX #6: Entry parsed and saved with AI macros:', {
              entryId: savedEntry.id,
              calories: analysis.calories,
              protein: analysis.protein,
              parseStatus: 'completed',
            });
            
            await removePendingEntry(entry.id);
            console.log('[OfflineSync] Nutrition entry synced:', entry.id);
            
            // BUG FIX #28: Return saved entry info to update recents
            return {
              success: true,
              savedEntryId: savedEntry.id,
              savedMacros: updatedEntry.macros,
              pendingId: entry.id,
            };
          } catch (aiError) {
            // BUG FIX #6: If AI processing fails again, keep entry in queue for retry
            // Don't save as-is - we want to retry parsing later
            console.error('[OfflineSync] BUG FIX #6: Failed to parse entry, will retry later:', {
              entryId: entry.id,
              error: aiError,
            });
            
            // Update retry count but keep entry in queue
            const newRetryCount = entry.retryCount + 1;
            if (newRetryCount < MAX_RETRY_COUNT) {
              await updateRetryCount(entry.id, newRetryCount);
              console.log('[OfflineSync] BUG FIX #6: Entry will retry parsing:', entry.id, 'attempt', newRetryCount);
              return { success: false }; // Return false so it's counted as failed and will retry
            } else {
              // Max retries reached - save as-is with placeholder macros
              console.error('[OfflineSync] BUG FIX #6: Max retries reached, saving entry with placeholder macros:', entry.id);
              const savedEntry = await saveNutritionEntryDirect(entry.userId, {
                ...nutritionEntry,
                parseStatus: 'failed' as const, // Mark as failed after max retries
              }, entry.date);
              await removePendingEntry(entry.id);
              return {
                success: true,
                savedEntryId: savedEntry.id,
                savedMacros: nutritionEntry.macros,
                pendingId: entry.id,
              };
            }
          }
        } else {
          // Non-placeholder entry - save directly
          const savedEntry = await saveNutritionEntryDirect(entry.userId, nutritionEntry, entry.date);
          await removePendingEntry(entry.id);
          console.log('[OfflineSync] Nutrition entry synced:', entry.id);
          return {
            success: true,
            savedEntryId: savedEntry.id,
            savedMacros: nutritionEntry.macros,
            pendingId: entry.id,
          };
        }
      } else {
        // No macros object - save as-is (shouldn't happen, but handle gracefully)
        const savedEntry = await saveNutritionEntryDirect(entry.userId, nutritionEntry, entry.date);
        await removePendingEntry(entry.id);
        return {
          success: true,
          savedEntryId: savedEntry.id,
          savedMacros: nutritionEntry.macros,
          pendingId: entry.id,
        };
      }
    } else if (entry.type === 'workout') {
      const workoutEntry = entry.entryData as Omit<WorkoutEntry, 'id' | 'userId' | 'date' | 'timestamp' | 'createdAt' | 'updatedAt'>;
      
      // Bug Fix: CRITICAL - Ensure type is explicitly set and preserved
      // If type is missing, try to infer from description or default to 'weightlifting' for gym entries
      let workoutType = workoutEntry.type;
      
      if (!workoutType || workoutType === 'manual') {
        // Try to infer type from description
        const description = (workoutEntry.description || workoutEntry.pastedText || '').toLowerCase();
        if (description.includes('gym') || description.includes('weight') || description.includes('strength') || description.includes('lifting')) {
          workoutType = 'weightlifting';
          console.log('[OfflineSync] Inferred workout type as weightlifting from description');
        } else if (description.includes('run') || description.includes('running')) {
          workoutType = 'run';
          console.log('[OfflineSync] Inferred workout type as run from description');
        } else if (description.includes('walk') || description.includes('walking')) {
          workoutType = 'walk';
          console.log('[OfflineSync] Inferred workout type as walk from description');
        } else if (description.includes('cycle') || description.includes('cycling') || description.includes('bike')) {
          workoutType = 'cycle';
          console.log('[OfflineSync] Inferred workout type as cycle from description');
        } else {
          workoutType = 'manual';
          console.warn('[OfflineSync] Workout entry missing type, defaulting to manual:', {
            entryId: entry.id,
            description: workoutEntry.description,
          });
        }
      }
      
      // Ensure type is explicitly set in the entry
      const entryWithType = {
        ...workoutEntry,
        type: workoutType,
      };
      
      // Log the type being synced to verify it's preserved
      console.log('[OfflineSync] Syncing workout entry with type:', {
        entryId: entry.id,
        type: entryWithType.type,
        description: workoutEntry.description,
      });
      
      await saveWorkoutEntryDirect(entry.userId, entryWithType, entry.date);
      await removePendingEntry(entry.id);
      console.log('[OfflineSync] Workout entry synced:', entry.id, 'type preserved:', entryWithType.type);
      return {
        success: true,
      };
    }
    return { success: false };
  } catch (error) {
    console.error('[OfflineSync] Failed to sync entry:', entry.id, error);
    
    // BUG FIX: Offline Logging Reconciliation - Enhanced error handling and retry logic
    // Check if error is a network error (should retry) vs validation error (should not retry)
    const isNetworkError = error instanceof Error && (
      error.message.includes('network') ||
      error.message.includes('fetch') ||
      error.message.includes('timeout') ||
      error.message.includes('Failed to fetch') ||
      error.name === 'NetworkError' ||
      error.name === 'TypeError'
    );
    
    // Only retry on network errors, not validation errors
    if (isNetworkError) {
      const newRetryCount = entry.retryCount + 1;
      if (newRetryCount < MAX_RETRY_COUNT) {
        await updateRetryCount(entry.id, newRetryCount);
        console.log('[OfflineSync] Network error - entry will retry:', entry.id, 'attempt', newRetryCount);
      } else {
        // Max retries reached - remove from queue to prevent infinite retries
        console.error('[OfflineSync] Max retries reached for entry (network errors):', entry.id);
        await removePendingEntry(entry.id);
      }
    } else {
      // Validation or other non-retryable error - remove immediately
      console.error('[OfflineSync] Non-retryable error - removing entry from queue:', entry.id, error);
      await removePendingEntry(entry.id);
    }
    
    return { success: false };
  }
}

/**
 * BUG FIX #6: Retry parsing entries in Firestore with parseStatus='pending'
 */
export async function retryPendingParseEntries(userId: string): Promise<{ success: number; failed: number }> {
  if (!navigator.onLine) {
    console.log('[OfflineSync] BUG FIX #6: Device is offline, skipping parse retry');
    return { success: 0, failed: 0 };
  }

  try {
    const { getCollection } = await import('./firebase/firestore.service');
    const { where } = await import('firebase/firestore');
    const { updateDocument } = await import('./firebase/firestore.service');
    const { normalizeNutritionEntry } = await import('../utils/nutrition');
    
    // Query Firestore for entries with parseStatus='pending'
    const pendingParseEntries = await getCollection<NutritionEntry & Record<string, unknown>>(
      'nutritionEntries',
      where('userId', '==', userId),
      where('parseStatus', '==', 'pending'),
    );
    
    // Found pending parse entries - no verbose logging needed
    
    let success = 0;
    let failed = 0;
    
    for (const entryData of pendingParseEntries) {
      const entry = normalizeNutritionEntry(entryData as NutritionEntry & Record<string, unknown>);
      
      if (!entry.id || !entry.description) {
        console.warn('[OfflineSync] BUG FIX #6: Skipping entry without id or description:', entry);
        continue;
      }
      
      try {
        // Try to parse with AI
        const analysis = await analyzeNutrition({
          textDescription: entry.description,
          userId: entry.userId,
        });
        
        // Update entry with parsed macros
        const updatedEntry = {
          ...entry,
          macros: {
            calories: analysis.calories || 0,
            protein: analysis.protein || 0,
            carbs: analysis.carbs || 0,
            fat: analysis.fat || 0,
            fibre: analysis.fibre || 0,
          },
          aiExtracted: true,
          source: entry.source || 'text-ai-estimate',
          parseStatus: 'completed' as const,
        };
        
        // Update in Firestore
        await updateDocument(`nutritionEntries/${entry.id}`, {
          macros: updatedEntry.macros,
          aiExtracted: updatedEntry.aiExtracted,
          source: updatedEntry.source,
          parseStatus: 'completed',
        });
        
        console.log('[OfflineSync] BUG FIX #6: Successfully parsed entry:', {
          entryId: entry.id,
          description: entry.description,
          calories: analysis.calories,
        });
        
        success++;
      } catch (error) {
        console.error('[OfflineSync] BUG FIX #6: Failed to parse entry, will retry later:', {
          entryId: entry.id,
          description: entry.description,
          error,
        });
        failed++;
        // Don't update parseStatus - keep it as 'pending' for next retry
      }
    }
    
    return { success, failed };
  } catch (error) {
    console.error('[OfflineSync] BUG FIX #6: Failed to retry pending parse entries:', error);
    return { success: 0, failed: 0 };
  }
}

/**
 * Sync all pending entries
 */
export async function syncAllPendingEntries(userId: string): Promise<{ success: number; failed: number }> {
  if (!navigator.onLine) {
    console.log('[OfflineSync] Device is offline, skipping sync');
    return { success: 0, failed: 0 };
  }

  // BUG FIX #6: Also retry parsing entries in Firestore with parseStatus='pending'
  await retryPendingParseEntries(userId);

  const entries = await getPendingEntries(userId);

  let success = 0;
  let failed = 0;
  const syncResults: SyncResult[] = [];

  // BUG FIX: Offline Logging Reconciliation - Sync entries sequentially with idempotency protection
  // Each entry has a unique ID (idempotency key) to prevent duplicates if sync is retried
  for (const entry of entries) {
    try {
      const result = await syncPendingEntry(entry);
      if (result.success) {
        success++;
        syncResults.push(result);
        console.log('[OfflineSync] Successfully synced entry:', entry.id);
      } else {
        failed++;
        console.warn('[OfflineSync] Failed to sync entry (will retry):', entry.id);
      }
    } catch (error) {
      // Catch any unexpected errors during sync
      failed++;
      console.error('[OfflineSync] Unexpected error syncing entry:', entry.id, error);
    }
    
    // Small delay between syncs to avoid rate limiting
    if (entries.indexOf(entry) < entries.length - 1) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }

  // Sync complete - no verbose logging needed for averages debugging
  
  // BUG FIX #28: Update recents in Firestore with synced entry IDs and macros
  // BUG FIX v34 Issue D: Update Firestore instead of localStorage for persistent storage
  if (syncResults.length > 0) {
    try {
      // Import the service dynamically to avoid circular dependencies
      const { getRecentsFavourites, saveRecents } = await import('./recentsFavourites.service');
      
      const data = await getRecentsFavourites(userId);
      if (data && data.recents) {
        const recents = data.recents;
        
        let updated = false;
        console.log('[OfflineSync] BUG FIX v34 Issue D: Checking recents for updates:', {
          syncResultsCount: syncResults.length,
          recentsCount: recents.length,
        });
        
        for (const result of syncResults) {
          if (result.pendingId && result.savedEntryId && result.savedMacros) {
            console.log('[OfflineSync] Looking for recent with pendingId:', result.pendingId);
            console.log('[OfflineSync] Available recents:', recents.map(r => ({
              id: r.id,
              nutritionEntryId: r.nutritionEntryId,
              description: r.description,
              calories: r.macros?.calories,
            })));
            
            // Find recent entry with matching pending ID
            // BUG FIX #28: Match by exact pending ID stored in nutritionEntryId
            const recentIndex = recents.findIndex(r => {
              const matches = r.nutritionEntryId === result.pendingId;
              if (matches) {
                console.log('[OfflineSync] Found matching recent:', {
                  recentId: r.id,
                  nutritionEntryId: r.nutritionEntryId,
                  pendingId: result.pendingId,
                });
              }
              return matches;
            });
            
            if (recentIndex >= 0) {
              const oldMacros = recents[recentIndex].macros;
              // Update with server ID and correct macros
              recents[recentIndex].nutritionEntryId = result.savedEntryId;
              recents[recentIndex].macros = result.savedMacros;
              updated = true;
              console.log('[OfflineSync] ✅ Updated recent entry:', {
                recentIndex,
                pendingId: result.pendingId,
                serverId: result.savedEntryId,
                oldCalories: oldMacros?.calories,
                newCalories: result.savedMacros.calories,
              });
            } else {
              console.log('[OfflineSync] ❌ No matching recent found for pendingId:', result.pendingId);
              console.log('[OfflineSync] This might mean the recent was stored with a different ID format or was deleted');
            }
          } else {
            console.log('[OfflineSync] Skipping result (missing data):', {
              hasPendingId: !!result.pendingId,
              hasSavedEntryId: !!result.savedEntryId,
              hasSavedMacros: !!result.savedMacros,
            });
          }
        }
        
        if (updated) {
          // BUG FIX v34 Issue D: Save to Firestore (persistent storage)
          await saveRecents(userId, recents);
          console.log('[OfflineSync] BUG FIX v34 Issue D: Recents updated in Firestore');
          
          // Also update localStorage as backup/cache
          try {
            const RECENTS_STORAGE_KEY = `coretrak.recents.${userId}`;
            localStorage.setItem(RECENTS_STORAGE_KEY, JSON.stringify(recents));
          } catch (localError) {
            console.warn('[OfflineSync] Failed to cache recents in localStorage (non-critical)', localError);
          }
          
          // Dispatch custom event to notify components to refresh recents
          window.dispatchEvent(new CustomEvent('recents-updated'));
        }
      } else {
        // Fallback: try localStorage if Firestore doesn't have data yet
        const RECENTS_STORAGE_KEY = `coretrak.recents.${userId}`;
        const recentsRaw = localStorage.getItem(RECENTS_STORAGE_KEY);
        if (recentsRaw) {
          const recents = JSON.parse(recentsRaw) as Array<{
            id: string;
            nutritionEntryId?: string;
            macros?: { calories: number; protein: number; carbs: number; fat: number; fibre: number };
          }>;
          
          let updated = false;
          for (const result of syncResults) {
            if (result.pendingId && result.savedEntryId && result.savedMacros) {
              const recentIndex = recents.findIndex(r => r.nutritionEntryId === result.pendingId);
              if (recentIndex >= 0) {
                recents[recentIndex].nutritionEntryId = result.savedEntryId;
                recents[recentIndex].macros = result.savedMacros;
                updated = true;
              }
            }
          }
          
          if (updated) {
            localStorage.setItem(RECENTS_STORAGE_KEY, JSON.stringify(recents));
            console.log('[OfflineSync] Recents updated in localStorage (fallback)');
            window.dispatchEvent(new CustomEvent('recents-updated'));
          }
        }
      }
    } catch (error) {
      console.error('[OfflineSync] BUG FIX v34 Issue D: Failed to update recents in Firestore:', error);
      // Fallback to localStorage if Firestore fails
      try {
        const RECENTS_STORAGE_KEY = `coretrak.recents.${userId}`;
        const recentsRaw = localStorage.getItem(RECENTS_STORAGE_KEY);
        if (recentsRaw) {
          const recents = JSON.parse(recentsRaw) as Array<{
            id: string;
            nutritionEntryId?: string;
            macros?: { calories: number; protein: number; carbs: number; fat: number; fibre: number };
          }>;
          
          let updated = false;
          for (const result of syncResults) {
            if (result.pendingId && result.savedEntryId && result.savedMacros) {
              const recentIndex = recents.findIndex(r => r.nutritionEntryId === result.pendingId);
              if (recentIndex >= 0) {
                recents[recentIndex].nutritionEntryId = result.savedEntryId;
                recents[recentIndex].macros = result.savedMacros;
                updated = true;
              }
            }
          }
          
          if (updated) {
            localStorage.setItem(RECENTS_STORAGE_KEY, JSON.stringify(recents));
            window.dispatchEvent(new CustomEvent('recents-updated'));
          }
        }
      } catch (fallbackError) {
        console.error('[OfflineSync] Failed to update recents in localStorage fallback:', fallbackError);
      }
    }
  }
  
  // BUG FIX: Offline Logging Reconciliation - Refresh UI immediately after sync
  // This ensures entries appear in history, totals update, and pending indicators clear
  if (success > 0 || failed > 0) {
    try {
      // 1. Invalidate daily summary cache to force refresh
      const { invalidateDailySummaryCache } = await import('../hooks/useDailySummary');
      invalidateDailySummaryCache();
      console.log('[OfflineSync] Daily summary cache invalidated after sync');
      
      // 2. Dispatch custom event to notify all components to refresh
      // This triggers immediate UI updates in Nutrition, Home, and Exercise screens
      window.dispatchEvent(new CustomEvent('offline-sync-complete', {
        detail: { success, failed, userId }
      }));
      console.log('[OfflineSync] Dispatched offline-sync-complete event:', { success, failed });
      
      // 3. Force a refresh of daily summary after a short delay to ensure Firestore writes are visible
      // This is critical for entries to appear in history and totals to update
      setTimeout(async () => {
        try {
          const { invalidateDailySummaryCache: invalidateCache } = await import('../hooks/useDailySummary');
          invalidateCache();
          // Dispatch another event to trigger refresh in components that use useDailySummary
          window.dispatchEvent(new CustomEvent('daily-summary-refresh'));
          console.log('[OfflineSync] Forced daily summary refresh after sync delay');
        } catch (error) {
          console.error('[OfflineSync] Failed to force daily summary refresh:', error);
        }
      }, 500);
    } catch (error) {
      console.error('[OfflineSync] Failed to refresh UI after sync:', error);
    }
  }
  
  return { success, failed };
}

/**
 * Queue entry for offline sync
 */
export async function queueForSync(
  type: EntryType,
  userId: string,
  entryData: Record<string, unknown>,
  date?: string,
): Promise<PendingEntry> {
  const pendingEntry = await queueEntry(type, userId, entryData, date);
  
  // Register Background Sync if supported
  if (isBackgroundSyncSupported()) {
    await registerBackgroundSync();
  }
  
  return pendingEntry;
}

/**
 * Try to sync immediately if online, otherwise queue
 */
/**
 * Connectivity check — delegates to the Firestore write itself.
 * The fetch-based probe was intercepted by the service worker and falsely
 * returned offline, silently queuing all writes to IndexedDB instead of
 * writing to Firebase. navigator.onLine is the correct signal here;
 * if a write fails on a live connection, saveWithOfflineSupport catches
 * the error and queues it then.
 */
async function isActuallyOnline(): Promise<boolean> {
  return navigator.onLine;
}

export async function saveWithOfflineSupport<T, E extends Record<string, unknown> = Record<string, unknown>>(
  type: EntryType,
  userId: string,
  entryData: E,
  saveFn: (userId: string, entryData: E, date?: string) => Promise<T>,
  date?: string,
): Promise<T & { pending?: boolean; pendingId?: string }> {
  // Sprint C Fix: Enhanced offline detection with better error handling
  let online = false;
  try {
    online = await isActuallyOnline();
  } catch (error) {
    // If online check fails, assume offline and queue
    console.warn('[OfflineSync] Online check failed, assuming offline:', error);
    online = false;
  }
  
  if (online) {
    try {
      // Try to save directly
      const result = await saveFn(userId, entryData, date);
      return result as T & { pending?: boolean; pendingId?: string };
    } catch (error) {
      // If save fails (network error, server error, etc.), queue for retry
      console.warn('[OfflineSync] Save failed, queueing for retry:', error);
      try {
        const pendingEntry = await queueForSync(type, userId, entryData, date);
        return { pending: true, pendingId: pendingEntry.id } as T & { pending?: boolean; pendingId?: string };
      } catch (queueError) {
        // If queueing also fails, log but don't throw - return a pending entry anyway
        console.error('[OfflineSync] Failed to queue entry, but returning pending state:', queueError);
        // Return a pending entry with a generated ID so UI can show it
        return { 
          pending: true, 
          pendingId: `${type}_${userId}_${Date.now()}_pending` 
        } as T & { pending?: boolean; pendingId?: string };
      }
    }
  } else {
    // Offline - queue immediately
    console.log('[OfflineSync] Device offline, queueing entry');
    try {
      const pendingEntry = await queueForSync(type, userId, entryData, date);
      return { pending: true, pendingId: pendingEntry.id } as T & { pending?: boolean; pendingId?: string };
    } catch (queueError) {
      // If queueing fails, log but don't throw - return a pending entry anyway
      console.error('[OfflineSync] Failed to queue entry, but returning pending state:', queueError);
      // Return a pending entry with a generated ID so UI can show it
      return { 
        pending: true, 
        pendingId: `${type}_${userId}_${Date.now()}_pending` 
      } as T & { pending?: boolean; pendingId?: string };
    }
  }
}

